<template>
  <div class="page-container">
    <div class="toolbar">
      <el-button type="primary" @click="openModal()">新建菜单</el-button>
    </div>
    <el-table stripe :data="menuTree" border row-key="id" style="width: 100%" :tree-props="{ children: 'children', hasChildren: 'hasChildren' }">
      <el-table-column prop="menuName" label="菜单名称" min-width="180" />
      <el-table-column prop="menuType" label="类型" width="100">
        <template #default="scope">
          <el-tag :type="scope.row.menuType === 1 ? 'info' : 'success'">
            {{ scope.row.menuType === 1 ? '目录' : '菜单' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="icon" label="图标" width="120" align="center">
        <template #default="scope">
          <el-icon v-if="scope.row.icon">
            <component :is="getIcon(scope.row.icon)" />
          </el-icon>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="path" label="菜单路径" min-width="180" />
      <el-table-column prop="parentName" label="上级菜单" min-width="140" />
      <el-table-column prop="sortOrder" label="排序号" width="100" align="center" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="scope">
          <el-button size="small" @click="editMenu(scope.row)">编辑</el-button>
          <el-button size="small" type="danger" @click="handleDeleteMenu(scope.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="菜单信息" v-model="dialogVisible" width="500px">
      <el-form :model="formData" label-width="100px">
        <el-form-item label="菜单名称" required>
          <el-input v-model="formData.menuName" placeholder="请输入菜单名称" />
        </el-form-item>
        <el-form-item label="菜单类型" required>
          <el-select v-model="formData.menuType">
            <el-option label="目录" :value="1" />
            <el-option label="菜单" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item label="上级菜单">
          <el-select v-model="formData.parentId" placeholder="请选择上级菜单">
            <el-option :value="0" label="无" />
            <el-option v-for="menu in parentMenus" :key="menu.id" :value="menu.id" :label="menu.menuName" />
          </el-select>
        </el-form-item>
        <el-form-item label="图标">
          <div class="icon-picker-trigger" @click="iconPickerVisible = true">
            <el-icon v-if="formData.icon" :size="20"><component :is="getIcon(formData.icon)" /></el-icon>
            <span v-else class="icon-placeholder">点击选择图标</span>
          </div>
        </el-form-item>
        <el-form-item label="菜单路径">
          <el-input v-model="formData.path" placeholder="如：/index" />
        </el-form-item>
        <el-form-item label="排序号">
          <el-input-number v-model="formData.sortOrder" :min="1" :max="99" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveMenu">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog title="选择图标" v-model="iconPickerVisible" width="760px" append-to-body>
      <div class="icon-grid">
        <div
          v-for="name in iconNames"
          :key="name"
          class="icon-cell"
          :class="{ active: formData.icon === name }"
          :title="name"
          @click="selectIcon(name)"
        >
          <el-icon :size="22"><component :is="ElementPlusIconsVue[name]" /></el-icon>
        </div>
      </div>
      <template #footer>
        <el-button @click="iconPickerVisible = false">取消</el-button>
        <el-button type="primary" @click="iconPickerVisible = false">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { getMenuList, saveMenu, deleteMenu } from '../api'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'

const menus = ref([])
const dialogVisible = ref(false)
const iconPickerVisible = ref(false)
const iconNames = Object.keys(ElementPlusIconsVue)
const formData = reactive({
  id: null,
  menuName: '',
  menuType: 2,
  parentId: 0,
  icon: '',
  path: '',
  sortOrder: 1
})

const parentMenus = computed(() => {
  return menus.value.filter(m => m.menuType === 1)
})

const menuTree = computed(() => {
  const tree = []
  const map = new Map()
  menus.value.forEach(menu => {
    map.set(menu.id, { ...menu, children: [], hasChildren: false })
  })
  menus.value.forEach(menu => {
    if (menu.parentId !== 0) {
      const parent = map.get(menu.parentId)
      if (parent) {
        parent.hasChildren = true
        parent.children.push(menu)
      }
    }
  })
  map.forEach(menu => {
    if (menu.parentId === 0) {
      tree.push(menu)
    }
  })
  return tree.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
})

onMounted(() => {
  loadMenus()
})

const loadMenus = async () => {
  try {
    const res = await getMenuList()
    menus.value = res.data
  } catch (error) {
    ElMessage.error('加载菜单失败')
  }
}

const getIcon = (iconName) => {
  return ElementPlusIconsVue[iconName] || null
}

const selectIcon = (name) => {
  formData.icon = name
  iconPickerVisible.value = false
}

const openModal = () => {
  Object.assign(formData, { id: null, menuName: '', menuType: 2, parentId: 0, icon: '', path: '', sortOrder: 1 })
  dialogVisible.value = true
}

const editMenu = (row) => {
  Object.assign(formData, row)
  dialogVisible.value = true
}

const handleSaveMenu = async () => {
  if (!formData.menuName) {
    ElMessage.warning('请输入菜单名称')
    return
  }
  try {
    await saveMenu(formData)
    ElMessage.success('保存成功')
    dialogVisible.value = false
    loadMenus()
  } catch (error) {
    ElMessage.error('保存失败')
  }
}

const handleDeleteMenu = async (id) => {
  try {
    await deleteMenu(id)
    ElMessage.success('删除成功')
    loadMenus()
  } catch (error) {
    ElMessage.error('删除失败')
  }
}
</script>

<style scoped>
.menu-container {
  padding: 20px;
}

.toolbar {
  margin-bottom: 20px;
}

.menu-container :deep(.el-table) {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.menu-container :deep(.el-table__header-wrapper) {
  background: #f5f7fa;
}

.menu-container :deep(.el-table th) {
  background: #f5f7fa !important;
  font-weight: 600;
}

.icon-picker-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  background: #fff;
  box-sizing: border-box;
}

.icon-picker-trigger:hover {
  border-color: #409eff;
}

.icon-placeholder {
  color: #c0c4cc;
  font-size: 14px;
}

.icon-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 8px;
  max-height: 420px;
  overflow-y: auto;
}

.icon-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  cursor: pointer;
  color: #606266;
}

.icon-cell:hover {
  background: #f5f7fa;
  color: #409eff;
}

.icon-cell.active {
  border-color: #409eff;
  background: #ecf5ff;
  color: #409eff;
}
</style>